/**
 * @fileoverview Unlink Diagram Command
 * 
 * Command to remove a link from a shape to another diagram.
 * 
 * @see Issue #789 - Universal Diagram Linking System
 */

import { ICommand } from './ICommand';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { getCookie } from '../../lib/cookies';

interface UnlinkDiagramCommandParams {
  sourceDiagramId: string;
  sourceShapeId: string;
  linkId: string;
}

export class UnlinkDiagramCommand implements ICommand {
  id: string;
  name = 'Unlink Diagram';
  description?: string;
  timestamp: number;
  
  private originalAttrs: Record<string, any> | null = null;
  private linkData: {
    linkedDiagramId: string;
    linkedDiagramName: string;
    linkId: string;
    linkType: string;
  } | null = null;

  constructor(private params: UnlinkDiagramCommandParams) {
    this.id = `unlink-diagram-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.description = 'Remove diagram link';
    this.timestamp = Date.now();
  }

  async execute(): Promise<void> {
    const mutationManager = getNodeMutationManager();
    const node = mutationManager.getNode(this.params.sourceShapeId);
    
    if (!node) {
      throw new Error(`Shape ${this.params.sourceShapeId} not found`);
    }

    // Store original attrs and link data for undo
    this.originalAttrs = { ...((node as any).attrs || {}) };
    this.linkData = {
      linkedDiagramId: (this.originalAttrs as any).linkedDiagramId,
      linkedDiagramName: (this.originalAttrs as any).linkedDiagramName,
      linkId: (this.originalAttrs as any).linkId,
      linkType: (this.originalAttrs as any).linkType || 'subprocess',
    };

    // Delete link via API
    const token = getCookie('gravity_token');
    if (!token) {
      throw new Error('Not authenticated');
    }

    // Delete link by shapeId (more reliable than linkId which might be stale)
    console.log('[UnlinkDiagramCommand] Deleting link by shapeId:', { 
      sourceShapeId: this.params.sourceShapeId,
      sourceDiagramId: this.params.sourceDiagramId,
      storedLinkId: this.params.linkId
    });

    const response = await fetch(
      `/api/shapes/${encodeURIComponent(this.params.sourceShapeId)}/link?sourceDiagramId=${encodeURIComponent(this.params.sourceDiagramId)}`,
      {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
      },
    );

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ error: 'Failed to delete link' }));
      console.error('[UnlinkDiagramCommand] Delete by shapeId failed:', { 
        status: response.status, 
        error: errorBody,
        sourceShapeId: this.params.sourceShapeId 
      });
      throw new Error(errorBody.error || 'Failed to delete link');
    }

    console.log('[UnlinkDiagramCommand] Link deleted successfully, updating shape attrs');

    // Remove link metadata from shape (set to undefined so mutation manager clears keys)
    const updatedAttrs = {
      linkedDiagramId: undefined,
      linkedDiagramName: undefined,
      linkId: undefined,
      linkType: undefined,
    } as Record<string, any>;

    console.log('[UnlinkDiagramCommand] Updated attrs patch:', { 
      before: this.originalAttrs,
      after: updatedAttrs,
      sourceShapeId: this.params.sourceShapeId 
    });

    // Update shape via mutation manager
    mutationManager.applyNodeChange(this.params.sourceShapeId, {
      attrs: updatedAttrs,
    } as any);

    console.log('[UnlinkDiagramCommand] Shape updated via mutation manager');

    // Emit event to trigger UI refresh
    try {
      const event = new CustomEvent('diagram:link:unlinked', {
        detail: {
          shapeId: this.params.sourceShapeId,
          diagramId: this.params.sourceDiagramId,
        },
      });
      window.dispatchEvent(event);
      console.log('[UnlinkDiagramCommand] Emitted unlink event');
    } catch (error) {
      console.warn('[UnlinkDiagramCommand] Failed to emit event:', error);
    }
  }

  async undo(): Promise<void> {
    if (!this.linkData) {
      // Link data wasn't stored, nothing to undo
      return;
    }

    const mutationManager = getNodeMutationManager();

    // Recreate link via API
    const token = getCookie('gravity_token');
    if (!token || !this.linkData) {
      return;
    }

    try {
      const input = {
        sourceDiagramId: this.params.sourceDiagramId,
        sourceShapeId: this.params.sourceShapeId,
        targetDiagramId: this.linkData.linkedDiagramId,
        linkType: this.linkData.linkType,
      };

      const response = await fetch(`/api/diagrams/${this.params.sourceDiagramId}/diagram-links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(input),
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        const newLinkId = result.link.id;

        // Restore shape attrs with new link ID
        const restoredAttrs = {
          ...this.originalAttrs,
          linkedDiagramId: this.linkData.linkedDiagramId,
          linkedDiagramName: this.linkData.linkedDiagramName,
          linkId: newLinkId,
          linkType: this.linkData.linkType,
        };

        mutationManager.applyNodeChange(this.params.sourceShapeId, {
          attrs: restoredAttrs,
        } as any);
      }
    } catch (error) {
      console.error('Failed to recreate link on undo:', error);
    }
  }

  async redo(): Promise<void> {
    await this.execute();
  }

  getMetadata(): Record<string, any> {
    return {
      sourceShapeId: this.params.sourceShapeId,
      linkId: this.params.linkId,
      linkData: this.linkData,
    };
  }
}
